import { useCallback, useEffect, useRef } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

import {
  SERVICE_WORKER_UPDATE_CHECK_INTERVAL_MS,
  SERVICE_WORKER_UPDATE_CHECK_MIN_INTERVAL_MS,
  SKIP_WAITING_TIMEOUT_MS,
  STARTUP_UPDATE_GRACE_MS,
} from "../constants";
import { reloadApplication } from "../utils/reloadApplication";

type ServiceWorkerUpdate = {
  applyUpdate: () => void;
  checkForUpdate: () => Promise<void>;
  isUpdateAvailable: boolean;
};

export const useServiceWorkerUpdate = (): ServiceWorkerUpdate => {
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(undefined);
  const swScriptUrlRef = useRef<string | undefined>(undefined);
  const lastUpdateCheckAtRef = useRef(0);
  const isReloadingRef = useRef(false);
  const hasRequestedUpdateRef = useRef(false);

  const startReload = useCallback((): void => {
    if (isReloadingRef.current) {
      return;
    }
    isReloadingRef.current = true;
    reloadApplication();
  }, []);

  const {
    needRefresh: [isUpdateAvailable, setIsUpdateAvailable],
    updateServiceWorker,
  } = useRegisterSW({
    onNeedReload: startReload,
    onRegisteredSW: (swScriptUrl, registration) => {
      swScriptUrlRef.current = swScriptUrl;
      registrationRef.current = registration;
    },
  });

  const checkForUpdate = useCallback(async (): Promise<void> => {
    const registration = registrationRef.current;
    const swScriptUrl = swScriptUrlRef.current;

    if (!registration || !swScriptUrl || registration.installing !== null || !navigator.onLine) {
      return;
    }

    lastUpdateCheckAtRef.current = Date.now();

    try {
      const response = await fetch(swScriptUrl, { cache: "no-store" });
      if (response.status !== 200) {
        return;
      }
      await registration.update();
    } catch {
      return;
    }
  }, []);

  useEffect(() => {
    const checkForUpdateWhenNotCheckedRecently = (): void => {
      if (Date.now() - lastUpdateCheckAtRef.current < SERVICE_WORKER_UPDATE_CHECK_MIN_INTERVAL_MS) {
        return;
      }
      checkForUpdate();
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        checkForUpdateWhenNotCheckedRecently();
      }
    };

    const interval = setInterval(checkForUpdate, SERVICE_WORKER_UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", checkForUpdateWhenNotCheckedRecently);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", checkForUpdateWhenNotCheckedRecently);
    };
  }, [checkForUpdate]);

  useEffect(() => {
    const isDetectedRightAfterPageLoad = performance.now() < STARTUP_UPDATE_GRACE_MS;

    if (!isUpdateAvailable || !isDetectedRightAfterPageLoad) {
      return;
    }

    updateServiceWorker();
    setIsUpdateAvailable(false);
  }, [isUpdateAvailable, setIsUpdateAvailable, updateServiceWorker]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    const reloadOnceNewWorkerHasTakenControl = (): void => {
      if (hasRequestedUpdateRef.current) {
        startReload();
      }
    };

    navigator.serviceWorker.addEventListener("controllerchange", reloadOnceNewWorkerHasTakenControl);

    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", reloadOnceNewWorkerHasTakenControl);
    };
  }, [startReload]);

  const applyUpdate = useCallback((): void => {
    if (isReloadingRef.current) {
      return;
    }
    hasRequestedUpdateRef.current = true;
    updateServiceWorker();
    setTimeout(startReload, SKIP_WAITING_TIMEOUT_MS);
  }, [startReload, updateServiceWorker]);

  return { applyUpdate, checkForUpdate, isUpdateAvailable };
};
